import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => {
    try { return sessionStorage.getItem('gm_token') || localStorage.getItem('gm_token'); } catch { return null; }
  });

  const getHeaders = useCallback(() => {
    const t = token;
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, [token]);

  useEffect(() => {
    const t = token;
    if (!t) { setLoading(false); return; }
    let cancelled = false;
    axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then(res => { if (!cancelled) setUser(res.data.user); })
      .catch(err => {
        console.error('Auth check failed:', err.message);
        if (!cancelled) {
          sessionStorage.removeItem('gm_token');
          localStorage.removeItem('gm_token');
          setToken(null);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const login = useCallback(async (email, password, role) => {
    const res = await axios.post(`${API}/api/auth/login`, { email, password, role });
    const { token: t, user: u } = res.data;
    sessionStorage.setItem('gm_token', t);
    localStorage.setItem('gm_token', t);
    setToken(t);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/api/auth/logout`, {}, { headers: getHeaders() }); }
    catch (err) { console.error('Logout request failed:', err.message); }
    sessionStorage.removeItem('gm_token');
    localStorage.removeItem('gm_token');
    setToken(null);
    setUser(null);
  }, [getHeaders]);

  const contextValue = useMemo(() => ({
    user, loading, login, logout, token, getHeaders
  }), [user, loading, login, logout, token, getHeaders]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be within AuthProvider');
  return ctx;
}
